import { getTimezonesGroupedByContinent, useTimezones } from "../utils";
import { marginBottom, marginLeft, marginTop, marginY, media, padding, paddingLeft, paddingY, typography } from "../../../../common/styles";
import React, { useContext, useState } from "react";
import { ActionType } from "../../../../AppDataProvider/actions";
import { AppContext } from "../../../../AppDataProvider/AppDataProvider";
import { applyOpacity } from "../../../../common/styles/utils";
import { Input } from "../../../../common/components";
import { requireBookingDataFields } from "../../../../utils";
import styled from "styled-components";
import { TimezoneSelectorBookingData } from "../TimezoneSelector";
import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
export type Timezone = {
  continent: string;
  dateAndTime: string;
  id: string;
  name: string;
};
export function TimezoneList() {
  const {
    t
  } = useTranslation();
  const timezones = useTimezones(t);
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  const [timezoneSearchText, setTimezoneSearchText] = useState<string>("");
  const {
    bookingData
  } = appData;
  requireBookingDataFields<TimezoneSelectorBookingData>(bookingData, ["timezone"]);
  const {
    timezone: selectedTimezone
  } = bookingData;
  const apolloClient = useApolloClient();
  const timezonesByContinent = getTimezonesGroupedByContinent(timezoneSearchText, timezones);
  const currentTimezone = timezones.find(tz => tz.id === selectedTimezone) as Timezone;
  const onSelectTimezone = (newTimezone: string) => {
    if (newTimezone !== selectedTimezone) {
      /**
       * Ensure the existing client cache is reset so we don't end up with a
       * mixture of timezones concatinated together.
       */
      apolloClient.clearStore().then(() => {
        appDataDispatch({
          payload: {
            updatedData: {
              timezone: newTimezone
            }
          },
          type: ActionType.CHANGE_SELECTED_TIMEZONE
        });
      }).catch(err => {
        console.error(err);
      });
    }
    appDataDispatch({
      type: ActionType.HIDE_NON_MODAL_DIALOG
    });
  };
  return <Container data-sentry-element="Container" data-sentry-component="TimezoneList" data-sentry-source-file="TimezoneList.tsx">
      <SelectedContainer data-sentry-element="SelectedContainer" data-sentry-source-file="TimezoneList.tsx">
        <SelectedName data-sentry-element="SelectedName" data-sentry-source-file="TimezoneList.tsx">{currentTimezone.name}</SelectedName>
        <SelectedDateAndTime data-sentry-element="SelectedDateAndTime" data-sentry-source-file="TimezoneList.tsx">{currentTimezone.dateAndTime}</SelectedDateAndTime>
      </SelectedContainer>
      <SearchInput aria-label={t("timezoneSearchTextAriaLabel")} name="timezoneSearchText" id="timezoneSearchText" placeholder={t("timezoneSearchPlaceholder")} onChange={(event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
      setTimezoneSearchText(event.target.value);
    }} value={timezoneSearchText} data-sentry-element="SearchInput" data-sentry-source-file="TimezoneList.tsx" />

      <ScrollableContainer data-sentry-element="ScrollableContainer" data-sentry-source-file="TimezoneList.tsx">
        {timezonesByContinent.map(({
        continent,
        timezones
      }) => {
        return <Section key={continent}>
              <Continent>{continent}</Continent>
              <TimezoneListContainer>
                {timezones.map(tz => {
              return <li key={tz.id}>
                      <TimezoneButton aria-labelledby={`${tz.id}-name`} onClick={() => onSelectTimezone(tz.id)}>
                        <Name id={`${tz.id}-name`}>{tz.name}</Name>
                        <DateAndTime>{tz.dateAndTime}</DateAndTime>
                      </TimezoneButton>
                    </li>;
            })}
              </TimezoneListContainer>
            </Section>;
      })}
      </ScrollableContainer>
    </Container>;
}
export default TimezoneList;
const ScrollableContainer = styled.div`
  max-height: 100%;
  overflow: hidden auto;
`;
const TimezoneListContainer = styled.div`
  max-height: 100%;
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-height: 100%;
`;
const SelectedContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const SelectedName = styled.div`
  ${typography.bodyBold}
  /*
   * Was unset — renders {currentTimezone.name} text directly with no
   * ambient colour, so it silently fell back to the browser default black.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const SelectedDateAndTime = styled.div`
  ${typography.body}
  /*
   * Was unset — renders {currentTimezone.dateAndTime} text directly with
   * no ambient colour.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};
`;
const SearchInput = styled(Input).attrs({
  variant: "text"
})``;
const Section = styled.ul`
  ${marginTop.m}
  ${marginBottom.zero}
  list-style: none;
  ${padding.zero}
`;
const Continent = styled.h4`
  ${typography.bodyBold}
  ${marginLeft.s}
  ${marginY.zero}
  &::first-letter {
    text-transform: capitalize;
  }
  /*
   * Was unset — renders the {continent} heading text directly with no
   * ambient colour, so it silently fell back to the browser default black.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const TimezoneButton = styled.button`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
  ${paddingLeft.s}
  ${paddingY.xs}

  background:none;
  border: none;

  @media ${media.hoverable} {
    &:hover {
      cursor: pointer;
      /*
       * Was applyOpacity(theme.colours.primary, 0.1) — an ad-hoc
       * approximation of a light primary tint using the legacy
       * (non-brand-reactive) colours.primary. --d-tint is the brand's own
       * purpose-built light-tint token (resolved from primaryLightColor),
       * so this now actually reacts to brand config instead of only ever
       * showing a diluted version of the hardcoded default primary.
       */
      background-color: ${({
  theme
}) => theme.brand.cssVars["--d-tint"]};
    }
  }

  &:focus {
    outline: none;
    border: 1px solid ${({
  theme
}) => theme.colours.dark};
  }
`;
const Name = styled.div`
  ${typography.small}
  text-align: left;
  /*
   * Was unset — renders {tz.name} text directly with no ambient colour.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const DateAndTime = styled.div`
  ${typography.small}
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6, false)};
  text-align: right;
`;